import _ from "lodash";
import { useState, useEffect } from "react";
import { FormProps, Modal, Form, Dropdown, TextArea, Button } from "semantic-ui-react";
import { useCurrentUser } from "../hooks/auth-hook";
import { ModalControlData, useRequest } from "../hooks/commonHooks";
import { trimSetField, useFormData } from "../hooks/formHooks";
import { restRequest } from "../http";
import { ENUM_TYPES_ISSUE_OVERVIEW_ROOT_CAUSE, FileContainerType } from "../models/common_model";
import { DropdownOption } from "../models/testing_model";
import { Config, FailMeasurement, IssueOverview } from "../models/issue_overview_model";
import { compareMeasurementName, fieldError } from "../utils";
import { FilesInput } from "./commons/FilesInput";
import { TextEditor } from "./commons/TextEditor";
import { IssueOverviewUnitsAffectedEditor } from "./IssueOverviewUnitsAffected";
import { LewittProjectDropdown } from "./commons/LewittProject";
import LewittAddableDropdown, { LewittRemoteSearchDropdown } from "./commons/LewittDropdown";
import { CommonEnumSelector } from "./commons/CommonEnum";

export function IssueOverviewEditor(props: { id?: number, modelControl: ModalControlData, onFinish: (data: IssueOverview) => void }) {
    const { id } = props;
    const [data, setField, setData] = useFormData<IssueOverview>({status: 1} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [loadingDropDownOptions, setLoadingDropDownOptions] = useState(false);
    const [configs] = useRequest<Config>('issue_overview/configs', undefined, 'get');
    const [failMeasurements, setFailMeasurements] = useState<string[]>([]);

    const isStatusClosed = data && data.status === 2;

    useEffect(() => {
        setInvalidError({});
        if (id) {
            setLoading(true);
            restRequest<IssueOverview>('issue_overview/' + id).then(d => {
                setData(d);
            }).finally(() => setLoading(false))
        } else {
            setData({status: 1} as any);
        }
    }, [id]);

    useEffect(() => {
        setLoadingDropDownOptions(true);
        Promise.all([ restRequest<string[]>('issue_overview/search_fail_measurements', {product: data.product}, 'post')]).then(([respFailMeasurements]) => {
            setFailMeasurements(respFailMeasurements.sort(compareMeasurementName));
        }).finally(() => setLoadingDropDownOptions(false));
    }, [data.product]);

    const isCreate = !id;
    const onSubmit = (event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();

        setLoading(true);

        let url: string;
        let formData = {...data};

        if (isCreate) {
            url = 'issue_overview/';
        } else {
            url = `issue_overview/${id}`
        }

        restRequest<IssueOverview>(url, formData, isCreate ? 'post' : 'put').then(result => {
            props.onFinish(result);
            if (isCreate) {
                setData({status: 1} as any);
            }
            setInvalidError({});
        }, resp => {
            if (resp.status === 2) {
                setInvalidError(resp.data);
            }
        }).finally(() => setLoading(false));
    }

    let actionName: string, buttonName: string;
    if (isCreate) {
        actionName = 'New';
        buttonName = 'Create';
    } else {
        actionName = 'Edit';
        buttonName = 'Save';
    }


    return <>
        <Modal {...props.modelControl.props} size="fullscreen">
            <Modal.Header>{actionName} Issue Overview</Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>
                    <Form.Input required label="Project" error={fieldError('product', invalidError)}>
                        {isCreate ? <LewittProjectDropdown value={data.product} onChange={value => setField(null, {name: 'product', value})} /> : <span>{data.product}</span>}
                    </Form.Input>
                    <Form.Group widths="equal">
                        <Form.Input label="Issue Name" name="name" value={data.name} onChange={setField} onBlur={trimSetField(setField)} required error={fieldError('name', invalidError)}></Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input required label="Failure Description" error={fieldError('description', invalidError, 'below')}>
                            <TextEditor name="description" value={data.description || ''} onChange={setField} />
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Buildname" error={fieldError('build_name', invalidError)}>
                            <LewittAddableDropdown value={data.build_name} onChange={setField} options={['TP1', 'TP2', 'PP1', 'PP2', 'MP'].map(v => ({text: v, value: v, key: v}))} name="build_name" />
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Units Affected" error={fieldError('serial_numbers', invalidError)}>
                            <TextEditor name="serial_numbers" value={data.serial_numbers || ''} onChange={setField} />
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Measurements Fail" error={fieldError('fail_measurements', invalidError)}>
                            <LewittAddableDropdown fluid selection search multiple name="fail_measurements" value={(data.fail_measurements || [])} onChange={setField} 
                            options={failMeasurements.map(r => ({ text: r, value: r, key: r }))} loading={loadingDropDownOptions}/>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input required={isStatusClosed} label="Failure Analysis" error={fieldError('analysis', invalidError, 'below')}>
                            <TextEditor name="analysis" value={data.analysis || ''} onChange={setField} />
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                         <CommonEnumSelector
                            typeId={ENUM_TYPES_ISSUE_OVERVIEW_ROOT_CAUSE}
                            label={"Root Cause"}
                            name="root_cause"
                            value={data.root_cause || ""}
                            onChange={setField}
                            required
                            error={fieldError("root_cause", invalidError)}
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input required={isStatusClosed} label="Corrective Actions" error={fieldError('actions', invalidError, 'below')}>
                            <TextEditor name="actions" value={data.actions || ''} onChange={setField} />
                        </Form.Input>
                    </Form.Group>

                    <Form.Group widths="equal">
                        <Form.Input required label="Status" error={fieldError('status', invalidError, 'below')}>
                            <Dropdown name="status" value={data.status || ''} onChange={setField} options={configs?.statuses.map((v, i) => ({key: i + 1, text: v, value: i + 1}))} selection fluid></Dropdown>
                        </Form.Input>

                        <Form.Input required label="Owner" value={data.owner_id || ''} error={fieldError('owner_id', invalidError, 'below')}>
                           <LewittRemoteSearchDropdown fluid selection name="owner_id" value={data.owner_id || ''} onChange={setField} url="users/dropdown_options" />
                        </Form.Input>

                        <Form.Input required={data.status === 2} label="Resolved By" value={data.resolved_by_id || ''} error={fieldError('resolved_by_id', invalidError, 'below')}>
                           <LewittRemoteSearchDropdown fluid selection name="resolved_by_id" value={data.resolved_by_id || ''} onChange={setField} url="users/dropdown_options" />
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Attachment">
                            <FilesInput name="files" value={data.files || []} onChange={setField} containerType={FileContainerType.TestingSummaryFile} containerId={id || 0}></FilesInput>
                        </Form.Input>
                    </Form.Group>

                    <Form.Group widths="equal">
                        <Form.Input label="Remark" error={fieldError('remark', invalidError, 'below')}>
                            <TextEditor name="remark" value={data.remark || ''} onChange={setField} />
                        </Form.Input>
                    </Form.Group>

                    <div>
                        <Button primary>{buttonName}</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => props.modelControl.setOpen(false)}>Close</Button>
                    </div>
                </Form>
            </Modal.Content>
        </Modal>
    </>
}